import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Mail, MessageSquare, ShieldOff } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type TotpStatus = {
  totpEnabled: boolean;
  totpEnrolledAt?: string | null;
  backupCodesRemaining?: number;
};

export type TotpDisableFlow =
  | { kind: "email-code"; sendCode: () => Promise<unknown>; recipientLabel: string }
  | { kind: "sms-code"; sendCode: () => Promise<unknown>; recipientLabel: string };

type Props = {
  basePath: string;
  testIdPrefix: string;
  disableFlow: TotpDisableFlow;
};

type EnrollStep = "idle" | "qr" | "confirm" | "backup";

export default function TotpManagementSection({ basePath, testIdPrefix, disableFlow }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [enrollStep, setEnrollStep] = useState<EnrollStep>("idle");
  const [qrData, setQrData] = useState<{ secret: string; qrCode: string } | null>(null);
  const [totpCode, setTotpCode] = useState(["", "", "", "", "", ""]);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);
  const [justEnrolledCodes, setJustEnrolledCodes] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedEnroll, setCopiedEnroll] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCodeSent, setDisableCodeSent] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disableCodeError, setDisableCodeError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const statusKey = [`${basePath}/status`];
  const totpStatusQuery = useQuery<TotpStatus>({ queryKey: statusKey });
  const status = totpStatusQuery.data;

  const startEnrollMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${basePath}/enroll-start`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Failed to start enrollment");
      return res.json() as Promise<{ secret: string; qrCode: string }>;
    },
    onSuccess: (data) => {
      setQrData(data);
      setTotpCode(["", "", "", "", "", ""]);
      setCodeError(null);
      setEnrollStep("qr");
    },
    onError: (err: Error) => {
      toast({ title: "Failed to start enrollment", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    },
  });

  const confirmEnrollMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiRequest("POST", `${basePath}/enroll-confirm`, { totpCode: code });
      return res.json() as Promise<{ success: boolean; backupCodes: string[] }>;
    },
    onSuccess: (data) => {
      setCodeError(null);
      setJustEnrolledCodes(data.backupCodes);
      setEnrollStep("backup");
      queryClient.invalidateQueries({ queryKey: statusKey });
      toast({ title: "2FA enabled", description: "Two-factor authentication is now active." });
    },
    onError: (err: Error) => {
      setCodeError(parseErrorMessage(err, "Invalid code. Please try again."));
      setTotpCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    },
  });

  const regenMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", `${basePath}/regenerate-backup-codes`, {});
      return res.json() as Promise<{ success: boolean; backupCodes: string[] }>;
    },
    onSuccess: (data) => {
      setNewBackupCodes(data.backupCodes);
      queryClient.invalidateQueries({ queryKey: statusKey });
      toast({ title: "Backup codes regenerated", description: "Save your new backup codes securely." });
    },
    onError: (err: Error) => {
      toast({ title: "Failed", description: parseErrorMessage(err, "Could not regenerate backup codes."), variant: "destructive" });
    },
  });

  const sendDisableCodeMutation = useMutation({
    mutationFn: async () => disableFlow.sendCode(),
    onSuccess: () => {
      setDisableCodeSent(true);
      setDisableCodeError("");
    },
    onError: (err: Error) => setDisableCodeError(parseErrorMessage(err, "Failed to send verification code.")),
  });

  const disableMutation = useMutation({
    mutationFn: async (code: string) => {
      const payload = disableFlow.kind === "email-code" ? { emailCode: code } : { smsCode: code };
      const res = await apiRequest("POST", `${basePath}/disable`, payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "2FA disabled", description: "Two-factor authentication has been turned off." });
      queryClient.invalidateQueries({ queryKey: statusKey });
      setDisableDialogOpen(false);
      setDisableCodeSent(false);
      setDisableCode("");
      setDisableCodeError("");
      setEnrollStep("idle");
      setJustEnrolledCodes(null);
      setNewBackupCodes(null);
    },
    onError: (err: Error) => setDisableCodeError(parseErrorMessage(err, "Invalid or expired code.")),
  });

  const handleTotpChange = (i: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    if (codeError) setCodeError(null);
    const next = [...totpCode];
    next[i] = value.slice(-1);
    setTotpCode(next);
    if (value && i < 5) inputRefs.current[i + 1]?.focus();
    const full = next.join("");
    if (full.length === 6) confirmEnrollMutation.mutate(full);
  };

  const handleTotpKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !totpCode[i] && i > 0) inputRefs.current[i - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    if (codeError) setCodeError(null);
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = [...totpCode];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || "";
    setTotpCode(next);
    if (pasted.length === 6) confirmEnrollMutation.mutate(pasted);
  };

  const SendIcon = disableFlow.kind === "email-code" ? Mail : MessageSquare;

  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-1">Two-Factor Authentication</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Add an extra layer of security to your account. When enabled, you'll need a 6-digit code from your authenticator app each time you sign in.{" "}
        <span className="font-medium text-foreground">(Recommended)</span>
      </p>

      {totpStatusQuery.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
      ) : (
        <>
          <div className="border rounded-lg p-4 bg-muted space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${status?.totpEnabled ? "bg-green-100 text-green-700" : "bg-muted-foreground/10 text-muted-foreground"}`}
                data-testid={`status-${testIdPrefix}-totp`}
              >
                {status?.totpEnabled ? "Enabled" : "Not enabled"}
              </span>
            </div>
            {status?.totpEnabled && status.totpEnrolledAt && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Enabled since</span>
                <span className="text-sm text-foreground">{new Date(status.totpEnrolledAt).toLocaleDateString()}</span>
              </div>
            )}
            {status?.totpEnabled && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Backup codes remaining</span>
                <span
                  className={`text-sm font-medium ${(status.backupCodesRemaining ?? 0) <= 2 ? "text-amber-600" : "text-foreground"}`}
                  data-testid={`text-${testIdPrefix}-backup-remaining`}
                >
                  {status.backupCodesRemaining ?? 0}
                </span>
              </div>
            )}
          </div>

          {!status?.totpEnabled && enrollStep === "idle" && (
            <Button
              onClick={() => startEnrollMutation.mutate()}
              disabled={startEnrollMutation.isPending}
              data-testid={`button-${testIdPrefix}-enable-totp`}
              className="bg-brand hover:bg-brand-hover text-white"
            >
              {startEnrollMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enable two-factor authentication
            </Button>
          )}

          {enrollStep === "qr" && qrData && (
            <div className="border rounded-lg p-5 bg-background space-y-4">
              <p className="text-sm font-semibold text-foreground">Step 1 — Scan this QR code with your authenticator app</p>
              <p className="text-xs text-muted-foreground">Use Google Authenticator, Authy, 1Password, or any TOTP-compatible app.</p>
              <div className="flex justify-center">
                <img src={qrData.qrCode} alt="TOTP QR Code" className="w-44 h-44 border border-border rounded-lg" data-testid={`img-${testIdPrefix}-totp-qr`} />
              </div>
              <p className="text-xs text-muted-foreground text-center">Can't scan? Enter this code manually:</p>
              <div className="bg-muted rounded-lg px-4 py-2 text-center">
                <span className="font-mono text-xs text-foreground break-all" data-testid={`text-${testIdPrefix}-totp-secret`}>{qrData.secret}</span>
              </div>
              <Button className="w-full bg-brand hover:bg-brand-hover text-white" onClick={() => setEnrollStep("confirm")} data-testid={`button-${testIdPrefix}-enroll-next`}>
                I've scanned the code — next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={() => { setEnrollStep("idle"); setQrData(null); }}
                data-testid={`button-${testIdPrefix}-enroll-cancel`}
              >
                Cancel
              </Button>
            </div>
          )}

          {enrollStep === "confirm" && (
            <div className="border rounded-lg p-5 bg-background space-y-4">
              <p className="text-sm font-semibold text-foreground">Step 2 — Enter the 6-digit code from your authenticator app</p>
              <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                {totpCode.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleTotpChange(i, e.target.value)}
                    onKeyDown={(e) => handleTotpKeyDown(i, e)}
                    autoFocus={i === 0}
                    className={`w-11 h-13 text-center text-xl font-bold border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-brand ${codeError ? "border-red-500" : "border-border"}`}
                    data-testid={`input-${testIdPrefix}-totp-${i}`}
                  />
                ))}
              </div>
              {codeError && <p className="text-xs text-red-600 text-center" data-testid={`error-${testIdPrefix}-totp-code`}>{codeError}</p>}
              {confirmEnrollMutation.isPending && <div className="flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setEnrollStep("qr")} data-testid={`button-${testIdPrefix}-enroll-back`}>Back to QR code</Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 text-muted-foreground"
                  onClick={() => { setEnrollStep("idle"); setQrData(null); setTotpCode(["","","","","",""]); setCodeError(null); }}
                  data-testid={`button-${testIdPrefix}-confirm-cancel`}
                >Cancel</Button>
              </div>
            </div>
          )}

          {enrollStep === "backup" && justEnrolledCodes && (
            <div className="border border-green-200 rounded-lg p-5 bg-green-50 space-y-3 mb-4">
              <p className="text-sm font-semibold text-green-800">Two-factor authentication enabled! Save your backup codes.</p>
              <p className="text-xs text-green-700">Each code can only be used once if you lose access to your authenticator app. Store them somewhere safe.</p>
              <div className="grid grid-cols-2 gap-2">
                {justEnrolledCodes.map((code, i) => (
                  <div key={i} className="font-mono text-sm bg-white border border-green-200 rounded px-3 py-1.5 text-center text-foreground" data-testid={`${testIdPrefix}-enrolled-backup-code-${i}`}>{code}</div>
                ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { navigator.clipboard.writeText(justEnrolledCodes.join("\n")); setCopiedEnroll(true); setTimeout(() => setCopiedEnroll(false), 2000); }}
                className="text-xs"
                data-testid={`button-${testIdPrefix}-copy-enrolled-codes`}
              >
                {copiedEnroll ? "Copied!" : "Copy all codes"}
              </Button>
            </div>
          )}

          {status?.totpEnabled && (
            <>
              {newBackupCodes && (
                <div className="border border-green-200 rounded-lg p-4 bg-green-50 mb-4">
                  <p className="text-sm font-semibold text-green-800 mb-3">New backup codes — save these now:</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {newBackupCodes.map((code, i) => (
                      <div key={i} className="font-mono text-sm bg-white border border-green-200 rounded px-3 py-1.5 text-center text-foreground" data-testid={`${testIdPrefix}-new-backup-code-${i}`}>{code}</div>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { navigator.clipboard.writeText(newBackupCodes.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="text-xs"
                    data-testid={`button-${testIdPrefix}-copy-new-codes`}
                  >
                    {copied ? "Copied!" : "Copy all codes"}
                  </Button>
                </div>
              )}

              <div className="flex gap-3 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => regenMutation.mutate()} disabled={regenMutation.isPending} data-testid={`button-${testIdPrefix}-regen-backup-codes`}>
                  {regenMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                  Regenerate backup codes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => { setDisableDialogOpen(true); setDisableCodeSent(false); setDisableCode(""); setDisableCodeError(""); }}
                  data-testid={`button-${testIdPrefix}-disable-totp`}
                >
                  <ShieldOff className="h-4 w-4 mr-1.5" />
                  Disable two-factor authentication
                </Button>
              </div>
            </>
          )}
        </>
      )}

      <Dialog open={disableDialogOpen} onOpenChange={(open) => { if (!open && (sendDisableCodeMutation.isPending || disableMutation.isPending)) return; setDisableDialogOpen(open); if (!open) { setDisableCodeSent(false); setDisableCode(""); setDisableCodeError(""); } }}>
        <DialogContent
          data-testid={`dialog-${testIdPrefix}-disable-totp`}
          onEscapeKeyDown={(e) => { if (sendDisableCodeMutation.isPending || disableMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (sendDisableCodeMutation.isPending || disableMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (sendDisableCodeMutation.isPending || disableMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication</DialogTitle>
            <DialogDescription>
              This will remove 2FA from your account. To confirm, we'll verify your identity via{" "}
              {disableFlow.kind === "email-code" ? "email" : "text message"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!disableCodeSent ? (
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  We'll send a one-time code to <strong>{disableFlow.recipientLabel}</strong>.
                </p>
                {disableCodeError && <p className="text-sm text-red-600 mb-2">{disableCodeError}</p>}
                <Button
                  onClick={() => sendDisableCodeMutation.mutate()}
                  disabled={sendDisableCodeMutation.isPending}
                  className="w-full"
                  data-testid={`button-${testIdPrefix}-send-disable-code`}
                >
                  {sendDisableCodeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <SendIcon className="h-4 w-4 mr-2" />}
                  Send verification code
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  Enter the code we sent to your {disableFlow.kind === "email-code" ? "email" : "phone"}:
                </p>
                <Input
                  value={disableCode}
                  onChange={(e) => { setDisableCode(e.target.value); setDisableCodeError(""); }}
                  placeholder="6-digit code"
                  maxLength={6}
                  className="text-center tracking-widest font-mono text-lg mb-2"
                  data-testid={`input-${testIdPrefix}-disable-code`}
                />
                {disableCodeError && <p className="text-sm text-red-600 mb-2">{disableCodeError}</p>}
                <p className="text-xs text-muted-foreground">
                  Didn't receive it?{" "}
                  <button
                    className="text-blue-600 hover:underline"
                    onClick={() => { setDisableCodeSent(false); setDisableCode(""); }}
                    data-testid={`link-${testIdPrefix}-resend-disable-code`}
                  >
                    Resend code
                  </button>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableDialogOpen(false)} data-testid={`button-${testIdPrefix}-cancel-disable`}>Cancel</Button>
            {disableCodeSent && (
              <Button
                variant="destructive"
                onClick={() => disableMutation.mutate(disableCode)}
                disabled={disableMutation.isPending || disableCode.length < 6}
                data-testid={`button-${testIdPrefix}-confirm-disable`}
              >
                {disableMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm disable
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
